import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Marker } from '@react-google-maps/api';
import SafeGoogleMap from './components/SafeGoogleMap';
import MapPermitCallout from './components/MapPermitCallout';
import { formatGovOpsError } from './api/govopsErrors';
import { useRobinhoodGoogleMaps } from './hooks/useRobinhoodGoogleMaps';
import MapsUnavailable from './components/MapsUnavailable';
import { getGoogleMapsKeyIssue } from './api/mapsConfig';
import { validateCoordinates } from './utils/mapCoordinates';
import { normalizeAlarmLocation } from './utils/permitFields';
import {
  INTAKE_MODES,
  INTAKE_SOURCES,
  submitPermitIntake,
  formatIntakeSuccess,
} from './utils/permitIntake';
import PageShell from './components/PageShell';
import FeeFieldsFormSection from './components/FeeFieldsFormSection';
import { defaultFeeFields } from './utils/feeFields';

const SubmitPermitForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    type: '',
    description: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    latitude: '',
    longitude: '',
    alarmLocation: { street: '', city: '', state: '', zipCode: '' },
    coordinates: null,
    ...defaultFeeFields(),
  });

  const [addressInput, setAddressInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const autocompleteRef = useRef(null);
  const autocompleteInstance = useRef(null);

  const { isLoaded, loadError } = useRobinhoodGoogleMaps();

  const syncCoordinatesFromFields = useCallback((latStr, lngStr) => {
    const check = validateCoordinates(latStr, lngStr);
    if (!check.ok) return null;
    return { latitude: check.lat, longitude: check.lng };
  }, []);

  const parseAddressComponents = useCallback((components) => {
    const result = {
      streetNumber: '',
      route: '',
      city: '',
      state: '',
      zipCode: '',
    };

    components.forEach((component) => {
      component.types.forEach((type) => {
        switch (type) {
          case 'street_number':
            result.streetNumber = component.long_name;
            break;
          case 'route':
            result.route = component.long_name;
            break;
          case 'locality':
          case 'postal_town':
            result.city = component.long_name;
            break;
          case 'administrative_area_level_1':
            result.state = component.short_name;
            break;
          case 'postal_code':
            result.zipCode = component.long_name;
            break;
          default:
            break;
        }
      });
    });

    return {
      street: [result.streetNumber, result.route].filter(Boolean).join(' '),
      city: result.city,
      state: result.state,
      zipCode: result.zipCode,
    };
  }, []);

  useEffect(() => {
    if (!isLoaded || !autocompleteRef.current || autocompleteInstance.current) return;

    const initAutocomplete = () => {
      try {
        if (!window.google?.maps?.places?.Autocomplete) {
          setApiError(
            'Address autocomplete is not available for this Maps key. Enter coordinates manually below or use Dashboard submit.'
          );
          return;
        }

        autocompleteInstance.current = new window.google.maps.places.Autocomplete(
          autocompleteRef.current,
          {
            types: ['address'],
            componentRestrictions: { country: 'us' },
            fields: ['address_components', 'geometry', 'formatted_address'],
          }
        );

        autocompleteInstance.current.addListener('place_changed', () => {
          const place = autocompleteInstance.current.getPlace();
          if (!place?.geometry?.location || !place.address_components) {
            setApiError('Please select a valid address from the dropdown.');
            return;
          }

          try {
            const addressData = parseAddressComponents(place.address_components);
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();

            setFormData((prev) => ({
              ...prev,
              street: addressData.street,
              city: addressData.city,
              state: addressData.state,
              zipCode: addressData.zipCode,
              latitude: lat.toFixed(6),
              longitude: lng.toFixed(6),
              coordinates: { latitude: lat, longitude: lng },
              alarmLocation: {
                street: addressData.street,
                city: addressData.city,
                state: addressData.state,
                zipCode: addressData.zipCode,
              },
            }));

            setAddressInput(place.formatted_address || '');
            setApiError(null);
          } catch (error) {
            console.error('Address processing error:', error);
            setApiError('Failed to process address');
          }
        });
      } catch (error) {
        console.error('Autocomplete error:', error);
        setApiError(
          'Address search unavailable. Enter latitude and longitude manually below.'
        );
      }
    };

    initAutocomplete();

    return () => {
      if (autocompleteInstance.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteInstance.current);
        autocompleteInstance.current = null;
      }
    };
  }, [isLoaded, parseAddressComponents]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setApiError(null);
    setSuccessMsg('');

    try {
      let coords = formData.coordinates;
      if (!coords && formData.latitude && formData.longitude) {
        const check = validateCoordinates(formData.latitude, formData.longitude);
        if (!check.ok) throw new Error(check.message);
        coords = { latitude: check.lat, longitude: check.lng };
      }

      if (!coords) {
        throw new Error(
          'Missing coordinates. Pick an address from search or enter latitude and longitude.'
        );
      }
      if (!formData.type?.trim() || !formData.description?.trim()) {
        throw new Error('Permit type and description are required.');
      }

      const mergedLoc = normalizeAlarmLocation({
        ...formData,
        alarmLocation: {
          ...formData.alarmLocation,
          street: formData.alarmLocation.street || formData.street,
          city: formData.alarmLocation.city || formData.city,
          state: formData.alarmLocation.state || formData.state,
          zipCode: formData.alarmLocation.zipCode || formData.zipCode,
        },
      });

      const intakeForm = {
        ...formData,
        type: formData.type,
        description: formData.description,
        alarmLocation: mergedLoc,
        street: mergedLoc.street,
        city: mergedLoc.city,
        state: mergedLoc.state,
        zipCode: mergedLoc.zipCode,
        coordinates: coords,
        latitude: String(coords.latitude),
        longitude: String(coords.longitude),
      };

      const result = await submitPermitIntake(intakeForm, {
        source: INTAKE_SOURCES.MAP_SUBMIT,
        mode: INTAKE_MODES.MAP,
      });

      setSuccessMsg(`${formatIntakeSuccess(result)} Opening map…`);
      setFormData({
        type: '',
        description: '',
        street: '',
        city: '',
        state: '',
        zipCode: '',
        latitude: '',
        longitude: '',
        alarmLocation: { street: '', city: '', state: '', zipCode: '' },
        coordinates: null,
      });
      setAddressInput('');

      setTimeout(() => navigate('/map', { replace: true }), 1200);
    } catch (error) {
      console.error('Submission error:', error);
      setApiError(formatGovOpsError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (['street', 'city', 'state', 'zipCode'].includes(name)) {
        next.alarmLocation = { ...prev.alarmLocation, [name]: value };
      }
      if (name === 'latitude' || name === 'longitude') {
        const lat = name === 'latitude' ? value : prev.latitude;
        const lng = name === 'longitude' ? value : prev.longitude;
        const synced = syncCoordinatesFromFields(lat, lng);
        next.coordinates = synced;
      }
      return next;
    });
  };

  const handleAlarmLocationChange = (e) => {
    const { name, value } = e.target;
    const field = name.split('.')[1];
    setFormData((prev) => ({
      ...prev,
      alarmLocation: { ...prev.alarmLocation, [field]: value },
    }));
  };

  const renderInput = (label, name, value, type = 'text', onChange, placeholder = '', required = false) => (
    <div className="mb-4">
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required ? ' *' : ''}
      </label>
      {type === 'textarea' ? (
        <textarea
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          disabled={isSubmitting}
          rows={3}
          required={required}
        />
      ) : (
        <input
          type={type}
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          disabled={isSubmitting}
          required={required}
        />
      )}
    </div>
  );

  const mapsBlocked = getGoogleMapsKeyIssue() || loadError;

  const previewPosition = (() => {
    if (formData.coordinates) {
      const lat = parseFloat(formData.coordinates.latitude);
      const lng = parseFloat(formData.coordinates.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    }
    const check = validateCoordinates(formData.latitude, formData.longitude);
    return check.ok ? { lat: check.lat, lng: check.lng } : null;
  })();

  return (
    <PageShell
      title="Submit permit (map)"
      subtitle="Search an address or enter coordinates. GovOps records the permit; the map preview is optional."
    >
      <div className="max-w-2xl mx-auto p-4 sm:p-6 bg-white rounded-lg shadow">
        <p className="rhs-muted text-sm mb-4">
          <Link to="/dashboard">Dashboard submit</Link>
          {' · '}
          <Link to="/map">View map</Link>
        </p>

        {mapsBlocked && <MapsUnavailable loadError={loadError} />}
        {!mapsBlocked && !isLoaded && (
          <div className="p-4 flex items-center justify-center" role="status">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            <span className="sr-only">Loading map…</span>
          </div>
        )}

        {apiError && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm" role="alert">
            {apiError}
          </div>
        )}
        {successMsg && (
          <div className="mb-4 p-3 bg-green-50 text-green-800 rounded-md text-sm" role="status">
            {successMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="google-autocomplete">
              Search address
            </label>
            <input
              ref={autocompleteRef}
              value={addressInput}
              onChange={(e) => setAddressInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Start typing address…"
              name="address"
              id="google-autocomplete"
              autoComplete="off"
            />
            <p className="rhs-muted text-xs mt-1">
              Select a suggestion from the dropdown. If search is unavailable, use coordinates below.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {renderInput('Latitude', 'latitude', formData.latitude, 'number', handleChange, '32.7157')}
            {renderInput('Longitude', 'longitude', formData.longitude, 'number', handleChange, '-117.1611')}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {renderInput('Street', 'street', formData.street, 'text', handleChange)}
            {renderInput('City', 'city', formData.city, 'text', handleChange)}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {renderInput('State', 'state', formData.state, 'text', handleChange)}
            {renderInput('Zip code', 'zipCode', formData.zipCode, 'text', handleChange)}
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Alarm location</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {renderInput('Street', 'alarmLocation.street', formData.alarmLocation.street, 'text', handleAlarmLocationChange)}
              {renderInput('City', 'alarmLocation.city', formData.alarmLocation.city, 'text', handleAlarmLocationChange)}
              {renderInput('State', 'alarmLocation.state', formData.alarmLocation.state, 'text', handleAlarmLocationChange)}
              {renderInput('Zip', 'alarmLocation.zipCode', formData.alarmLocation.zipCode, 'text', handleAlarmLocationChange)}
            </div>
          </div>

          {renderInput('Permit type', 'type', formData.type, 'text', handleChange, 'e.g. Residential', true)}
          {renderInput('Description', 'description', formData.description, 'textarea', handleChange, 'Permit details', true)}

          <FeeFieldsFormSection
            values={formData}
            onChange={setFormData}
            disabled={isSubmitting}
            idPrefix="submit-fee"
          />

          <button
            type="submit"
            className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md shadow-sm transition-colors duration-150 disabled:opacity-50"
            disabled={isSubmitting || !previewPosition}
          >
            {isSubmitting ? 'Submitting…' : 'Submit permit'}
          </button>
          {!previewPosition && (
            <p className="rhs-muted text-sm text-center">Enter valid coordinates to enable submit.</p>
          )}
        </form>

        {previewPosition && !mapsBlocked && isLoaded && (
          <div className="mt-8">
            <div className="h-64 rounded overflow-hidden shadow border">
              <SafeGoogleMap
                center={previewPosition}
                zoom={15}
                mapContainerStyle={{ width: '100%', height: '100%' }}
              >
                <Marker position={previewPosition} title={formData.type || 'Permit location'} />
                <MapPermitCallout
                  position={previewPosition}
                  title={formData.type || 'Permit location'}
                  lines={[
                    formData.description,
                    [formData.street, formData.city, formData.state, formData.zipCode]
                      .filter(Boolean)
                      .join(', '),
                  ].filter(Boolean)}
                />
              </SafeGoogleMap>
            </div>
          </div>
        )}

        {previewPosition && (
          <div className="mt-2 text-sm text-blue-600 underline text-center">
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${previewPosition.lat},${previewPosition.lng}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Google Maps
            </a>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default SubmitPermitForm;
